import { Button, Form, Select, Tooltip } from 'antd';
import { postApi, putApi } from 'apis';
import InputWithLabelAndSvg from 'components/Global/InputWithLabel';
import { FORMDATA } from 'constants/Data';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import { SideDrawerBodyProps } from './types';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { fetchConstantDropdownAction } from '../../../../redux/actions/constantDropdownAction';
import {
	FormValues,
	UserField,
	EditUserData,
	RootState,
	InviteUserPayload,
	UpdateUserPayload,
	Role,
	Branch,
} from 'interfaces/userBody.interface';
import { InfoCircleOutlined } from '@ant-design/icons';

// Constants
const ADMIN_ROLE_NAME = 'Admin';
const ERROR_MESSAGES = {
	INVITE_GENERIC: 'Something went wrong when trying to invite',
	UPDATE_GENERIC: 'Something went wrong in updating user',
	SELECT_ROLE: 'Please select role',
	SELECT_BRANCH: 'Please select at least one branch',
} as const;

const SUCCESS_MESSAGES = {
	USER_INVITED: 'User invited successfully',
	USER_UPDATED: 'User updated successfully',
} as const;

const AddUserBody: FC<SideDrawerBodyProps> = (props) => {
	// Destructured props
	const {
		closeDrawerByAnimation,
		editSelectedUser,
		roleOptions,
		fetchUsers,
	} = props;

	// Form instance
	const [form] = Form.useForm<FormValues>();

	// Redux
	const dispatch = useDispatch<AppDispatch>();
	const { data: dropdownOptions, isLoading: dropdownOptionsLoading } =
		useSelector((state: RootState) => state.constantDropdown);

	// State
	const [selectedRole, setSelectedRole] = useState<string | null>(
		editSelectedUser?.roleId ?? null
	);
	const [selectedBranches, setSelectedBranches] = useState<string[]>(
		editSelectedUser?.branchIds ?? []
	);
	const [isSelected, setIsSelected] = useState<boolean | undefined>(
		undefined
	);
	const [isBranchSelected, setIsBranchSelected] = useState<boolean>(true);
	const [loading, setLoading] = useState<boolean>(false);
	const [isAdmin, setIsAdmin] = useState<boolean>(false);

	// Memoized values
	const { addUserFields } = FORMDATA;

	const userFields = useMemo<UserField[]>(() => {
		if (editSelectedUser) {
			return addUserFields;
		}
		return addUserFields.filter(
			(item: UserField) => item.id !== 'fullName'
		);
	}, [editSelectedUser, addUserFields]);

	const editData: EditUserData = useMemo(
		() => ({
			...editSelectedUser,
			fullName: editSelectedUser?.name ?? '',
			branchIds: editSelectedUser?.branchIds ?? [],
		}),
		[editSelectedUser]
	);

	const filteredBranchOptions = useMemo(() => {
		return (
			dropdownOptions?.branchCode?.filter(
				(branch: Branch) => !invalidText(branch.value)
			) ?? []
		);
	}, [dropdownOptions?.branchCode]);

	// Helper functions
	const resetValidationState = useCallback((): void => {
		setIsSelected(undefined);
		setIsBranchSelected(true);
	}, []);

	const validateForm = useCallback(
		(values: FormValues): boolean => {
			let isValid = true;

			if (selectedRole) {
				setIsSelected(true);
			} else {
				setIsSelected(false);
				isValid = false;
			}

			if (
				!isAdmin &&
				(!selectedBranches || selectedBranches.length === 0)
			) {
				setIsBranchSelected(false);
				isValid = false;
			} else {
				setIsBranchSelected(true);
			}

			return isValid;
		},
		[selectedRole, isAdmin, selectedBranches]
	);

	const getErrorMessage = useCallback(
		(error: any, defaultMessage: string): string => {
			return error?.response?.data?.message ?? defaultMessage;
		},
		[]
	);

	// API functions
	const inviteUser = useCallback(
		async (finalData: InviteUserPayload): Promise<void> => {
			try {
				setLoading(true);
				await postApi('/user/invite-user', finalData);
				toastText(SUCCESS_MESSAGES.USER_INVITED, 'success');
			} catch (error: any) {
				const errorMessage = getErrorMessage(
					error,
					ERROR_MESSAGES.INVITE_GENERIC
				);
				toastText(errorMessage, 'error');
				throw error; // Re-throw to handle in calling function
			} finally {
				setLoading(false);
			}
		},
		[getErrorMessage]
	);

	const updateUser = useCallback(
		async (finalData: UpdateUserPayload): Promise<void> => {
			try {
				setLoading(true);
				await putApi('/user', finalData);
				toastText(SUCCESS_MESSAGES.USER_UPDATED, 'success');
			} catch (error: any) {
				const errorMessage = getErrorMessage(
					error,
					ERROR_MESSAGES.UPDATE_GENERIC
				);
				toastText(errorMessage, 'error');
				throw error; // Re-throw to handle in calling function
			} finally {
				setLoading(false);
			}
		},
		[getErrorMessage]
	);

	// Event handlers
	const handleRoleChange = useCallback(
		(role: string): void => {
			setSelectedRole(role);

			const selectedRoleData = roleOptions?.find(
				(r: Role) => r.id === role
			);
			const isAdminRole = selectedRoleData?.name === ADMIN_ROLE_NAME;

			setIsAdmin(isAdminRole);

			if (isAdminRole) {
				setSelectedBranches([]);
				setIsBranchSelected(true);
			}

			setIsSelected(true);
		},
		[roleOptions]
	);

	const handleBranchChange = useCallback((values: string[]): void => {
		setSelectedBranches(values);
		setIsBranchSelected(values.length > 0);
	}, []);

	const buildUpdatePayload = useCallback(
		(values: FormValues): UpdateUserPayload => {
			const fullName = (values.fullName as string).trim();
			let data: UpdateUserPayload = {
				userId: editSelectedUser?.userId ?? '',
				fullName,
			};

			if (selectedRole) {
				data = { ...data, roleId: selectedRole };
			}

			if (!isAdmin && selectedBranches.length > 0) {
				data = { ...data, branchIds: selectedBranches };
			}

			return data;
		},
		[editSelectedUser?.userId, selectedRole, isAdmin, selectedBranches]
	);

	const buildInvitePayload = useCallback(
		(values: FormValues): InviteUserPayload => {
			const finalData: InviteUserPayload = {
				email: values.email ?? '',
				role: selectedRole!,
			};

			if (!isAdmin && selectedBranches.length > 0) {
				finalData.branchIds = selectedBranches;
			}

			return finalData;
		},
		[selectedRole, isAdmin, selectedBranches]
	);

	const handleEditUser = useCallback(
		async (values: FormValues): Promise<void> => {
			const updatePayload = buildUpdatePayload(values);
			await updateUser(updatePayload);
			fetchUsers();
			closeDrawerByAnimation();
		},
		[buildUpdatePayload, updateUser, fetchUsers, closeDrawerByAnimation]
	);

	const handleInviteUser = useCallback(
		async (values: FormValues): Promise<void> => {
			if (!selectedRole) {
				setIsSelected(false);
				return;
			}

			setIsSelected(true);
			const invitePayload = buildInvitePayload(values);
			await inviteUser(invitePayload);
			fetchUsers();
			closeDrawerByAnimation();
		},
		[
			selectedRole,
			buildInvitePayload,
			inviteUser,
			fetchUsers,
			closeDrawerByAnimation,
		]
	);

	const onFinish = useCallback(
		async (values: FormValues): Promise<void> => {
			if (!validateForm(values)) {
				return;
			}

			try {
				if (editSelectedUser) {
					await handleEditUser(values);
				} else {
					await handleInviteUser(values);
				}
			} catch (error) {
				// Error is already handled in the respective functions
				console.error('Form submission error:', error);
			}
		},
		[validateForm, editSelectedUser, handleEditUser, handleInviteUser]
	);

	const onFinishFailed = useCallback((): void => {
		if (selectedRole) {
			setIsSelected(true);
		} else {
			setIsSelected(false);
		}

		if (!isAdmin && (!selectedBranches || selectedBranches.length === 0)) {
			setIsBranchSelected(false);
		}
	}, [selectedRole, isAdmin, selectedBranches]);

	const handleFormReset = useCallback((): void => {
		resetValidationState();
		closeDrawerByAnimation();
	}, [resetValidationState, closeDrawerByAnimation]);

	// Effects
	useEffect(() => {
		if (editSelectedUser) {
			const findRole = roleOptions.find(
				(role: Role) => role.id === editSelectedUser?.roleId
			);
			setIsAdmin(findRole?.name === ADMIN_ROLE_NAME);
		}
	}, [editSelectedUser, roleOptions]);

	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	// Render helpers
	const renderRoleSelect = useCallback(
		(singleField: UserField) => (
			<Form.Item
				className={styles['side-drawer-form__role']}
				name={singleField.name}
				key={singleField.id}
			>
				<label className={styles['side-drawer-form__role--label']}>
					{singleField.title}{' '}
					{singleField?.required && (
						<span className="required-color">&nbsp;*</span>
					)}
				</label>

				<Select
					placeholder="Select Role"
					className={styles['side-drawer-form__role--select']}
					size="large"
					disabled={false}
					value={selectedRole}
					onSelect={handleRoleChange}
				>
					{roleOptions?.map((role: Role) => (
						<Select.Option value={role?.id} key={role?.id}>
							{role?.name}
						</Select.Option>
					))}
				</Select>
				{isSelected === false && (
					<p className="ant-form-item-explain-error">
						{ERROR_MESSAGES.SELECT_ROLE}
					</p>
				)}
			</Form.Item>
		),
		[roleOptions, selectedRole, handleRoleChange, isSelected]
	);

	const renderBranchSelect = useCallback(() => {
		if (isAdmin) return null;

		return (
			<Form.Item
				className={styles['side-drawer-form__role']}
				name="branchIds"
			>
				<p className={styles['side-drawer-form__role--label']}>
					Select Branches
					<span className="required-color">&nbsp;*&nbsp;</span>
					<Tooltip
						title="Branches selected for this role -only for that branch user will be able to access the system."
						placement="right"
					>
						<InfoCircleOutlined
							style={{
								paddingInline: '0.5rem',
								color: '#584495',
								cursor: 'pointer',
							}}
						/>
					</Tooltip>
				</p>

				<Select
					placeholder="Select Branches"
					className={styles['side-drawer-form__role--select']}
					size="large"
					disabled={dropdownOptionsLoading}
					loading={dropdownOptionsLoading}
					mode="multiple"
					value={selectedBranches}
					onChange={handleBranchChange}
					maxTagCount="responsive" // Shows "+N" for overflow tags
					maxTagPlaceholder={(omittedValues) => (
						<Tooltip
							title={omittedValues
								.map((v: any) => v.label)
								.join(', ')}
						>
							<span>+{omittedValues.length} more</span>
						</Tooltip>
					)}
					optionLabelProp="label" // Use label for display
					showSearch
					filterOption={(input, option) =>
						String(option?.label ?? '')
							.toLowerCase()
							.includes(input.toLowerCase())
					}
				>
					{filteredBranchOptions.map((branch: Branch) => (
						<Select.Option
							value={branch?.value}
							key={branch?.value}
							label={branch?.label} // For selected display
							title={branch?.label} // Tooltip on hover
						>
							<Tooltip title={branch?.label} placement="left">
								<div
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{branch?.label}
								</div>
							</Tooltip>
						</Select.Option>
					))}
				</Select>
				{isBranchSelected === false && (
					<p className="ant-form-item-explain-error">
						{ERROR_MESSAGES.SELECT_BRANCH}
					</p>
				)}
			</Form.Item>
		);
	}, [
		isAdmin,
		dropdownOptionsLoading,
		selectedBranches,
		handleBranchChange,
		filteredBranchOptions,
		isBranchSelected,
	]);

	const renderFormField = useCallback(
		(singleField: UserField) => {
			if (singleField.id === 'phone') {
				return (
					<Form.Item
						className={styles['side-drawer-form__role']}
						name={singleField.name}
						key={singleField.id}
					>
						<label
							className={styles['side-drawer-form__role--label']}
						>
							{singleField.title}{' '}
							{singleField?.required && (
								<span className="required-color">*</span>
							)}
						</label>
					</Form.Item>
				);
			}

			if (singleField.id === 'roleName') {
				return renderRoleSelect(singleField);
			}

			if (singleField.id !== 'phone') {
				return (
					<InputWithLabelAndSvg
						key={singleField.id}
						singleUserInput={singleField}
						disabled={
							singleField?.name === 'email' && !!editSelectedUser
						}
					/>
				);
			}

			return null;
		},
		[editSelectedUser, renderRoleSelect]
	);

	// JSX
	return (
		<div className={styles['side-drawer-body']}>
			<Form
				form={form}
				name="basic"
				initialValues={editData}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				layout="vertical"
				labelAlign="left"
				className={styles['side-drawer-form']}
			>
				<div className={styles['side-drawer-form__inputs']}>
					{userFields.map(renderFormField)}
					{renderBranchSelect()}
				</div>

				<div className={styles['side-drawer-form__buttons']}>
					<Form.Item>
						<Button
							htmlType="submit"
							className={
								styles['side-drawer-form__buttons--edit']
							}
							loading={loading}
							disabled={loading}
						>
							Save
						</Button>
					</Form.Item>
					<Form.Item>
						<Button
							htmlType="reset"
							className={
								styles['side-drawer-form__buttons--cancel']
							}
							onClick={handleFormReset}
							disabled={loading}
						>
							Cancel
						</Button>
					</Form.Item>
				</div>
			</Form>
		</div>
	);
};

export default AddUserBody;
